import { createSlice } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";

// Helper function to get token data from localStorage
const getTokenDataFromStorage = () => {
  try {
    const tokenData = JSON.parse(localStorage.getItem("tokenData")) || {};
    return {
      userId: tokenData.userId || "",
      firstName: tokenData.firstName || "",
      lastName: tokenData.lastName || "",
      email: tokenData.email || "",
      accessToken: tokenData.accessToken || null,
      refreshToken: tokenData.refreshToken || null,
    };
  } catch {
    return {
      userId: "",
      firstName: "",
      lastName: "",
      email: "",
      accessToken: null,
      refreshToken: null,
    };
  }
};

const storedData = getTokenDataFromStorage();

const initialState = {
  // User data from storage
  userId: storedData.userId,
  firstName: storedData.firstName,
  lastName: storedData.lastName,
  email: storedData.email,
  password: "",

  // Form errors
  firstNameError: "",
  lastNameError: "",
  emailError: "",
  passwordError: "",

  // Status messages
  signupError: "",
  signupSuccess: "",
  loginError: "",
  loginSuccess: "",
  passwordResetError: "",
  passwordResetSuccess: "",

  // Auth state
  accessToken: storedData.accessToken,
  refreshToken: storedData.refreshToken,
  isAuthenticated: Boolean(storedData.accessToken),
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setFirstName: (state, action) => {
      state.firstName = action.payload;
    },
    setLastName: (state, action) => {
      state.lastName = action.payload;
    },
    setEmail: (state, action) => {
      state.email = action.payload;
    },
    setPassword: (state, action) => {
      state.password = action.payload;
    },
    setFirstNameError: (state, action) => {
      state.firstNameError = action.payload;
    },
    setLastNameError: (state, action) => {
      state.lastNameError = action.payload;
    },
    setEmailError: (state, action) => {
      state.emailError = action.payload;
    },
    setPasswordError: (state, action) => {
      state.passwordError = action.payload;
    },
    setSignupError: (state, action) => {
      state.signupError = action.payload;
    },
    setSignupSuccess: (state, action) => {
      state.signupSuccess = action.payload;
    },
    setLoginError: (state, action) => {
      state.loginError = action.payload;
    },
    setLoginSuccess: (state, action) => {
      state.loginSuccess = action.payload;
    },
    setPasswordResetError: (state, action) => {
      state.passwordResetError = action.payload;
    },
    setPasswordResetSuccess: (state, action) => {
      state.passwordResetSuccess = action.payload;
    },
    setUser: (state, action) => {
      const { userId, firstName, lastName, email } = action.payload;
      state.userId = userId;
      state.firstName = firstName;
      state.lastName = lastName;
      state.email = email;

      // Update localStorage with new user data
      const existingData = JSON.parse(localStorage.getItem("tokenData")) || {};
      localStorage.setItem("tokenData", JSON.stringify({
        ...existingData,
        userId,
        firstName,
        lastName,
        email
      }));
    },
    setTokens: (state, action) => {
      const { accessToken, refreshToken, userId } = action.payload;
      
      // Update state
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.userId = userId;
      state.isAuthenticated = true;

      // Clear any existing errors
      state.loginError = "";
      state.signupError = "";

      // Update localStorage
      const existingData = JSON.parse(localStorage.getItem("tokenData")) || {};
      const tokenData = {
        ...existingData,
        accessToken,
        refreshToken,
        userId
      };
      localStorage.setItem("tokenData", JSON.stringify(tokenData));
    },
    clearTokens: (state) => {
      // Clear auth state
      state.accessToken = null;
      state.refreshToken = null;
      state.userId = "";
      state.firstName = "";
      state.lastName = "";
      state.email = "";
      state.password = "";
      state.isAuthenticated = false;

      // Clear error and success messages
      state.loginError = "";
      state.loginSuccess = "";
      state.signupError = "";
      state.signupSuccess = "";
      state.passwordResetError = "";
      state.passwordResetSuccess = "";

      // Clear localStorage
      localStorage.removeItem("tokenData");
    },
    clearErrors: (state) => {
      state.firstNameError = "";
      state.lastNameError = "";
      state.emailError = "";
      state.passwordError = "";
      state.loginError = "";
      state.signupError = "";
      state.passwordResetError = "";
    },
    clearSuccess: (state) => {
      state.loginSuccess = "";
      state.signupSuccess = "";
      state.passwordResetSuccess = "";
    }
  },
});

// Enhanced selectors
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectCurrentUser = (state) => ({
  userId: state.auth.userId,
  firstName: state.auth.firstName,
  lastName: state.auth.lastName,
  email: state.auth.email,
});
export const selectAccessToken = (state) => state.auth.accessToken;
export const selectRefreshToken = (state) => state.auth.refreshToken;
export const selectAuthErrors = (state) => ({
  firstNameError: state.auth.firstNameError,
  lastNameError: state.auth.lastNameError,
  emailError: state.auth.emailError,
  passwordError: state.auth.passwordError,
  loginError: state.auth.loginError,
  signupError: state.auth.signupError,
  passwordResetError: state.auth.passwordResetError,
});

// Export all actions
export const {
  setFirstName,
  setLastName,
  setEmail,
  setPassword,
  setFirstNameError,
  setLastNameError,
  setEmailError,
  setPasswordError,
  setSignupError,
  setSignupSuccess,
  setLoginError,
  setLoginSuccess,
  setPasswordResetError,
  setPasswordResetSuccess,
  setUser,
  setTokens,
  clearTokens,
  clearErrors,
  clearSuccess,
} = authSlice.actions;

export default authSlice.reducer;